import * as i2 from '@angular/cdk/portal';
import { CdkPortalOutlet, PortalModule, DomPortalOutlet, ComponentPortal, TemplatePortal } from '@angular/cdk/portal';
import { NgClass, DOCUMENT } from '@angular/common';
import * as i0 from '@angular/core';
import { InjectionToken, EventEmitter, Component, ChangeDetectionStrategy, CUSTOM_ELEMENTS_SCHEMA, ViewChild, Output, Input, ChangeDetectorRef, TemplateRef, Injectable, Inject } from '@angular/core';
import '@anacapri/elements/dist/components/dialog/dialog.js';
import { Subject, merge } from 'rxjs';
import { first } from 'rxjs/operators';
import { v4 } from 'uuid';

/**
 * Class representing the configurable settings that can be applied to a dialog.
 */
class AnaDialogConfig {
    /**
     * Unique identifier for the dialog.
     */
    dialogId;
    /**
     * CSS classes to apply to the dialog.
     *
     * @propType string | string[] | Set<string> | { [klass: string]: any }
     */
    classes;
    /**
     * The heading of the dialog window.
     */
    heading;
    /**
     * Close the dialog when clicking the backdrop.
     */
    backdropCancel;
    /**
     * Disable canceling the dialog with the Escape key.
     */
    disableEscapeCancel;
    /**
     * Hide the close button in the dialog header. Useful if you require a user to finish the dialog to proceed.
     */
    hideClose;
    /**
     * The width of the dialog box. UX prefers representing dialog width using Foundation grid 12 columns, we follow
     * that convention to be consistent. Any number 1 - 12 are valid, although anything lower than 5 isn’t recommended.
     */
    width;
    /**
     * The estimated height the dialog will be once content is loaded in. We set this height to the loading indicator
     * that is present when no content has been passed into the default slot.
     */
    heightEstimate;
    /**
     * Set the `z-index` of the `Dialog`. Generally, it's best to let Anacapri determine `zIndex` for you, but in some instances you may need to override the default behavior.
     * @since 10.1.0
     */
    zIndex;
    /**
     * Initial state object for providing any data properties to be made available as the context when using the template view
     * option.
     */
    context;
    /**
     * Arbitrary value to be made available via injection with the NGX_ANA_DIALOG_DATA InjectionToken into the view component.
     */
    data;
}
/**
 * Default values for a dialog config.
 */
const defaultDialogConfig = {
    heading: '',
    backdropCancel: false,
    disableEscapeCancel: false,
    hideClose: false,
    width: 8,
    heightEstimate: '500px',
};
const NGX_ANA_DIALOG_DATA = new InjectionToken('NGXAnaDialogData');
const NGX_ANA_DIALOG_CANCELLED = 'CANCELLED';

/**
 * Dialog component wrapping the Anacapri dialog component. Note, this component should not be used directly as it is controlled
 * via the {@link AnaDialogService} which is what should be utilized for instantiating dialogs.
 *
 * @status stable
 * @since 10.0.0
 * @author [#anacapri](https://auto-owners.slack.com/archives/CPJ9QQKPY) [#frontend](https://auto-owners.slack.com/archives/C1MM1N89H) [DL.IT.FrontEndDevelopment](mailto:DL.IT.FrontEndDevelopment) *
 * @see [AO_UI Dialog](https://wordpress.aoins.com/AOUI/dialog/)
 * @see [Anacapri Dialog](https://www.aoins.com/osc/dsda/dev-docs/teams/fed/anacapri/angular/dialog/)
 */
class AnaDialogComponent {
    injector;
    changeDetector;
    _open = false;
    dialogConfig;
    cdkPortalOutlet;
    /**
     * Event for when the dialog is opened.
     */
    opened = new EventEmitter();
    /**
     * Event for when the dialog is moved.
     */
    moved = new EventEmitter();
    /**
     * Event for when the dialog is cancelled.
     */
    cancelled = new EventEmitter();
    /**
     * Event for when the dialog is closed.
     */
    closed = new EventEmitter();
    get open() {
        return this._open;
    }
    /**
     * Set the open state of the dialog.
     *
     * @param open whether the dialog should be open or not
     */
    set open(open) {
        this._open = open;
        this.changeDetector.detectChanges();
    }
    /**
     * Wire in the necessary bits.
     *
     * @param dialogConfig - Config object containing the various settings for controlling the dialog.
     * @param injector - a reference to the Angular App's root injector for providing access to dependencies wired up in the app.
     * @param changeDetector - a reference to the component's change detector reference to allow for manually triggering change detection.
     */
    constructor(dialogConfig, injector, changeDetector) {
        this.injector = injector;
        this.changeDetector = changeDetector;
        this.dialogConfig = { ...defaultDialogConfig, ...dialogConfig };
    }
    /**
     * Used to attach a {@link ComponentPortal} to the general {@link CdkPortalOutlet} to display the passed in content component in the dialog.
     *
     * @param portal - the component portal to attach.
     */
    attachComponentPortal(portal) {
        return portal.attach(this.cdkPortalOutlet);
    }
    /**
     * Used to attach a {@link TemplatePortal} to the general {@link CdkPortalOutlet} to display the passed in content template in the dialog.
     *
     * @param portal - the template portal to attach.
     */
    attachTemplatePortal(portal) {
        return portal.attach(this.cdkPortalOutlet);
    }
    /**
     * Template event method for handling the 'open-finished' event which is triggered when the dialog has finished
     * opening.
     *
     * @param event - the custom 'open-finished' event.
     */
    dialogOpened(event) {
        this.opened.emit(event);
    }
    /**
     * Template event method for handling the 'move-finished' event which is triggered when the dialog has finished
     * moving.
     *
     * @param event - the custom 'move' event.
     */
    dialogMoved(event) {
        this.moved.emit(event);
    }
    /**
     * Template event method for handling the 'cancel' event which is triggered when the dialog has been cancelled.
     *
     * @param event - the custom 'cancel' event.
     */
    dialogCancelled(event) {
        this.cancelled.emit(event);
    }
    /**
     * Template event method for handling the 'close-finished' event which is triggered when the dialog finishes closing
     * and its animations have completed.
     *
     * @param event - the custom 'close-finished' event.
     */
    dialogClosed(event) {
        this.closed.emit(event);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnaDialogComponent, deps: [{ token: AnaDialogConfig }, { token: i0.Injector }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AnaDialogComponent, isStandalone: true, selector: "ngx-ana-dialog", inputs: { open: "open" }, outputs: { opened: "opened", moved: "moved", cancelled: "cancelled", closed: "closed" }, viewQueries: [{ propertyName: "cdkPortalOutlet", first: true, predicate: CdkPortalOutlet, descendants: true, static: true }], ngImport: i0, template: "<!-- Make sure that [open]=\"true\" always comes as the last input property before the event listeners as its order in the list of input properties\n     matters due to ana-dialog having a specialized watcher on the [open] input property which does initialization work which expects all other\n     input properties to have already been set. Putting [open] at the end of the list guarantees that angular will set all of the other input\n     properties first prior to setting [open]. The order that the other input properties are in does not matter at this time.-->\n<ana-dialog\n  [id]=\"dialogConfig.dialogId\"\n  [ngClass]=\"dialogConfig.classes\"\n  [backdropCancel]=\"dialogConfig.backdropCancel\"\n  [disableEscapeCancel]=\"dialogConfig.disableEscapeCancel\"\n  [heading]=\"dialogConfig.heading\"\n  [hideClose]=\"dialogConfig.hideClose\"\n  [zIndex]=\"dialogConfig.zIndex\"\n  [width]=\"dialogConfig.width\"\n  [heightEstimate]=\"dialogConfig.heightEstimate\"\n  [open]=\"open\"\n  (open-finished)=\"dialogOpened($event)\"\n  (move-finished)=\"dialogMoved($event)\"\n  (cancel)=\"dialogCancelled($event)\"\n  (close-finished)=\"dialogClosed($event)\"\n>\n  <ng-template cdkPortalOutlet></ng-template>\n</ana-dialog>\n", dependencies: [{ kind: "ngmodule", type: PortalModule }, { kind: "directive", type: i2.CdkPortalOutlet, selector: "[cdkPortalOutlet]", inputs: ["cdkPortalOutlet"], outputs: ["attached"], exportAs: ["cdkPortalOutlet"] }, { kind: "directive", type: NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnaDialogComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ngx-ana-dialog', changeDetection: ChangeDetectionStrategy.OnPush, standalone: true, imports: [PortalModule, NgClass], schemas: [CUSTOM_ELEMENTS_SCHEMA], template: "<!-- Make sure that [open]=\"true\" always comes as the last input property before the event listeners as its order in the list of input properties\n     matters due to ana-dialog having a specialized watcher on the [open] input property which does initialization work which expects all other\n     input properties to have already been set. Putting [open] at the end of the list guarantees that angular will set all of the other input\n     properties first prior to setting [open]. The order that the other input properties are in does not matter at this time.-->\n<ana-dialog\n  [id]=\"dialogConfig.dialogId\"\n  [ngClass]=\"dialogConfig.classes\"\n  [backdropCancel]=\"dialogConfig.backdropCancel\"\n  [disableEscapeCancel]=\"dialogConfig.disableEscapeCancel\"\n  [heading]=\"dialogConfig.heading\"\n  [hideClose]=\"dialogConfig.hideClose\"\n  [zIndex]=\"dialogConfig.zIndex\"\n  [width]=\"dialogConfig.width\"\n  [heightEstimate]=\"dialogConfig.heightEstimate\"\n  [open]=\"open\"\n  (open-finished)=\"dialogOpened($event)\"\n  (move-finished)=\"dialogMoved($event)\"\n  (cancel)=\"dialogCancelled($event)\"\n  (close-finished)=\"dialogClosed($event)\"\n>\n  <ng-template cdkPortalOutlet></ng-template>\n</ana-dialog>\n" }]
        }], ctorParameters: function () { return [{ type: AnaDialogConfig }, { type: i0.Injector }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { cdkPortalOutlet: [{
                type: ViewChild,
                args: [CdkPortalOutlet, { static: true }]
            }], opened: [{
                type: Output
            }], moved: [{
                type: Output
            }], cancelled: [{
                type: Output
            }], closed: [{
                type: Output
            }], open: [{
                type: Input
            }] } });

/**
 * Specialized model object created upon opening a dialog via {@link AnaDialogService#open} which contains various methods and
 * event emitters for use in interacting and controlling the dialog externally while it is open.
 */
class AnaDialogRef {
    domPortalOutlet;
    dialogWrapperComponentRef;
    dialogId;
    beforeClosedSubject = new Subject();
    afterClosedSubject = new Subject();
    /**
     * Event emitter that is triggered and notifies subscribers immediately before the dialog is closed, possibly emitting an arbitrary
     * result if one is passed to the close method often times being the results of the user input in the dialog.
     */
    beforeClosed;
    /**
     * Event emitter that is triggered and notifies subscribers immediately after the dialog is closed, possibly emitting an arbitrary
     * result if one is passed to the close method often times being the results of the user input in the dialog.
     */
    afterClosed;
    /**
     * Event emitter that is triggered and notifies subscribers immediately after the dialog is opened. This even emitter
     * currently does not emit any results.
     */
    afterOpened;
    /**
     * Event emitter that is triggered and notifies subscribers when a dialog is moved due to another dialog being open
     * or closed changing the stacking order.
     */
    afterMoved;
    /**
     * A public reference to the component provided as the content to be displayed in the dialog upon opening it to provide
     * access to it post opening.
     */
    instance; // Note: this is actually set after the creation of the DialogRef itself as the component instance is not available at creation time.
    /**
     * Wire in the necessary bits.
     *
     * @param domPortalOutlet a reference to the dom portal the wrapping dialog component is attached to for removing the component when
     * the dialog is closed
     * @param dialogWrapperComponentRef a reference to the custom angular component which wraps the <ana-dialog> native component
     * to give us a hook for integrating native Angular features
     * @param dialogId The unique identifier created or provided for the dialog
     */
    constructor(domPortalOutlet, dialogWrapperComponentRef, dialogId) {
        this.domPortalOutlet = domPortalOutlet;
        this.dialogWrapperComponentRef = dialogWrapperComponentRef;
        this.dialogId = dialogId;
        merge(this.dialogWrapperComponentRef.instance.cancelled, this.dialogWrapperComponentRef.instance.closed)
            .pipe(first())
            .subscribe((result) => {
            if (result.type === 'cancel') {
                this.close(NGX_ANA_DIALOG_CANCELLED);
            }
            else {
                this.close(result);
            }
        });
        this.beforeClosed = this.beforeClosedSubject.asObservable();
        this.afterClosed = this.afterClosedSubject.asObservable();
        this.afterOpened = this.dialogWrapperComponentRef.instance.opened.asObservable();
        this.afterMoved = this.dialogWrapperComponentRef.instance.moved.asObservable();
    }
    /**
     * Close the dialog instance associated with this AnaDialogRef and optionally provide a result to be emitted via the
     * beforeClosed and afterClosed event emitters.
     *
     * If the dialog has been cancelled, pass the `NGX_ANA_DIALOG_CANCELLED` const as the result in order to properly
     * trigger the dialog service observables.
     *
     * @param result optional result to emit
     */
    close(result) {
        this.beforeClosedSubject.next(result);
        this.dialogWrapperComponentRef.instance.open = false;
        this.dialogWrapperComponentRef.injector.get(ChangeDetectorRef).detectChanges();
        setTimeout(() => {
            this.dialogWrapperComponentRef.destroy();
            this.domPortalOutlet.detach();
            this.afterClosedSubject.next(result);
        }, 150);
    }
}

/**
 * Specialized injector for use in custom dialog components to allow for injecting the {@link AnaDialogConfig},
 * {@link AnaDialogRef} and any other dependencies from the root injector.
 *
 * @author Matt Sukta e013867
 */
class AnaDialogInjector {
    parentInjector;
    additionalTokens;
    /**
     * Wire in the necessary bits.
     *
     * @param parentInjector - a reference to the app's root injector to look up all dependencies not directly passed to this injector.
     * @param additionalTokens - injector tokens for wiring up custom dependencies to be maid available for injection.
     */
    constructor(parentInjector, additionalTokens) {
        this.parentInjector = parentInjector;
        this.additionalTokens = additionalTokens;
    }
    get(token, notFoundValue) {
        return this.additionalTokens.get(token) ?? this.parentInjector.get(token, notFoundValue);
    }
}

/**
 * Service providing the primary API for creating, closing, being notified of events and interacting with Anacapri dialog components.
 * The design of the service is heavily inspired by the Angular Material MatDialog service and shares a similar API.  This service is
 * injectable anywhere within your angular app allowing for opening and managing dialogs from components, services or any other injectable
 * entity within the app.
 *
 * The service currently supports 2 formats (component or template ref) in which the content to display in dialog
 * can be provided to the service.  The component format is designed to allow you to create a completely separate component whose template
 * contains all the content you want displayed in the dialog.  The template ref format allows you to capture a portion of a template via
 * a template ref variable like #myTemplate and inject it into the component you want to open the dialog from via
 * @ViewChild('myTemplate', { read: TemplateRef }) which can then be passed to the template service to be opened in the dialog.
 *
 * @author [#anacapri]{@link https://auto-owners.slack.com/archives/CPJ9QQKPY} [#frontend]{@link https://auto-owners.slack.com/archives/C1MM1N89H} [DL.IT.FrontEndDevelopment]{@link mailto:DL.IT.FrontEndDevelopment}
 *
 * @see [AO_UI Dialog]{@link https://wordpress.aoins.com/AOUI/dialog/}
 * @see [Anacapri Dialog]{@link https://www.aoins.com/aoenv/anacapri/docs/latest/documentation/dialog/angular/}
 * @see [Angular MatDialog]{@link https://material.angular.io/components/dialog/api#MatDialog}
 */
class AnaDialogService {
    document;
    applicationRef;
    componentFactoryResolver;
    injector;
    afterAllClosedSubject = new Subject();
    afterAllClosedObservable = this.afterAllClosedSubject.asObservable();
    afterOpenedSubject = new Subject();
    afterOpenedObservable = this.afterOpenedSubject.asObservable();
    dialogRefs = new Map();
    dialogPortalOutlet;
    getDialogClosedHandler = (dialogId) => () => {
        this.dialogRefs.delete(dialogId);
        if (this.dialogRefs.size === 0) {
            this.afterAllClosedSubject.next();
        }
    };
    /**
     * Wire in the necessary bits.
     *
     * @param document - a reference to the browser's document element to get a reference to the body element to attach the dialog to.
     * @param applicationRef - a reference to the root application ref to get access to the component tree for attaching the dialog component to.
     * @param componentFactoryResolver - a reference to the component factory resolver for generating a component factory for dynamically generating
     * components on the fly.
     * @param injector - a reference to the app's root injector.
     */
    constructor(document, applicationRef, componentFactoryResolver, injector) {
        this.document = document;
        this.applicationRef = applicationRef;
        this.componentFactoryResolver = componentFactoryResolver;
        this.injector = injector;
        this.dialogPortalOutlet = this.document.createElement('div');
        this.dialogPortalOutlet.id = 'ngx-ana-dialog-portal-root';
        this.dialogPortalOutlet.dataset.anaInertIgnore = '';
        this.document.body.appendChild(this.dialogPortalOutlet);
    }
    /**
     * @internal
     */
    open(componentOrTemplate, config = { heading: '' }) {
        if (!config?.dialogId) {
            config.dialogId = v4();
        }
        const domPortalOutlet = new DomPortalOutlet(this.dialogPortalOutlet, this.componentFactoryResolver, this.applicationRef, new AnaDialogInjector(this.injector, new WeakMap([[AnaDialogConfig, config]])));
        const dialogComponentRef = domPortalOutlet.attach(new ComponentPortal(AnaDialogComponent));
        const dialogComponentInstance = dialogComponentRef.instance;
        const dialogRef = new AnaDialogRef(domPortalOutlet, dialogComponentRef, config.dialogId);
        if (componentOrTemplate instanceof TemplateRef) {
            const context = {
                $implicit: config.data,
                dialogRef,
                ...config.context,
            };
            dialogComponentInstance.attachTemplatePortal(new TemplatePortal(componentOrTemplate, null, context));
        }
        else {
            const dialogInjector = new AnaDialogInjector(this.injector, new WeakMap([
                [AnaDialogRef, dialogRef],
                [NGX_ANA_DIALOG_DATA, config.data ?? {}],
            ]));
            dialogRef.instance = dialogComponentInstance.attachComponentPortal(new ComponentPortal(componentOrTemplate, null, dialogInjector)).instance;
            if (config.context) {
                console.warn(`DialogConfig's 'context' property is meant for use with a template and should not be used when displaying a custom ` +
                    `component in the dialog. Dialog Header: "${config.heading}"; Dialog ID: ${config.dialogId}`);
            }
        }
        dialogComponentInstance.open = true;
        this.dialogRefs.set(dialogRef.dialogId, dialogRef);
        dialogRef.afterClosed.pipe(first()).subscribe(this.getDialogClosedHandler(dialogRef.dialogId));
        dialogRef.afterOpened.pipe(first()).subscribe(() => this.afterOpenedSubject.next(dialogRef));
        return dialogRef;
    }
    /**
     * Close a currently opened dialog via its dialogId.  Most of the time each dialog will be closed by the dialog itself
     * via one of the standard approaches such as clicking off of the dialog, hitting escape or pressing a button on the
     * dialog itself, but this method allows for closing a dialog from code outside the dialog itself.
     *
     * @param dialogId the unique reference id of the dialog to close
     * @param dialogResult a custom result to be emitted by the dialog's closing/closed event emitters
     */
    close(dialogId, dialogResult) {
        this.getDialogById(dialogId).close(dialogResult);
    }
    /**
     * Get a reference to a currently open dialog's AnaDialogRef based on its dialogId.
     *
     * @param dialogId the unique reference id of the dialog to retrieve
     */
    getDialogById(dialogId) {
        return this.dialogRefs.get(dialogId);
    }
    /**
     * Get a reference to an array of AnaDialogRefs for all currently opened dialogs.
     */
    openDialogs() {
        return Array.from(this.dialogRefs).map(([, value]) => value);
    }
    /**
     * Special method which can be called to close all currently open dialogs.  An example of when to use might be if
     * there are dialogs open and a route change occurs.
     */
    closeAll() {
        const openDialogs = this.openDialogs();
        if (openDialogs.length > 0) {
            openDialogs.forEach((dialogRef) => this.close(dialogRef.dialogId));
        }
        else {
            this.afterAllClosedSubject.next();
        }
    }
    /**
     * Gets a reference to an observable that can be subscribed to in order to be notified when all currently open dialogs have been
     * closed.  Note, this observable will fire in the typical scenario of opening and closing a single dialog to collect user input
     * once the dialog has been closed.
     */
    afterAllClosed() {
        return this.afterAllClosedObservable;
    }
    /**
     * Gets a reference to an observable that can be subscribed to in order to be notified after any dialog is opened.  Note, this observable
     * fires once after each dialog is opened regardless of how many dialogs are currently open.
     */
    afterOpened() {
        return this.afterOpenedObservable;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnaDialogService, deps: [{ token: DOCUMENT }, { token: i0.ApplicationRef }, { token: i0.ComponentFactoryResolver }, { token: i0.Injector }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnaDialogService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AnaDialogService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: i0.ApplicationRef }, { type: i0.ComponentFactoryResolver }, { type: i0.Injector }]; } });

const NGX_ANA_DIALOG_DIRECTIVES = [AnaDialogComponent, PortalModule];

/**
 * Generated bundle index. Do not edit.
 */

export { AnaDialogComponent, AnaDialogConfig, AnaDialogRef, AnaDialogService, NGX_ANA_DIALOG_CANCELLED, NGX_ANA_DIALOG_DATA, NGX_ANA_DIALOG_DIRECTIVES, defaultDialogConfig };
